import React, { useState, useEffect, useRef, useCallback } from 'react';

import { Button } from '@material-ui/core';
import { Add, Remove, Refresh } from '@material-ui/icons';
import { isEmpty, differenceBy, findIndex } from 'lodash';
import PropTypes from 'prop-types';

import { Table, Modal } from '~/components';
import { t } from '~/helpers';

import { MDate, MTable } from './components';
import * as S from './styles';

export default function SelectDataInTable({
  date,
  time,
  height,
  cells,
  size,
  onChanged,
  title,
  showCount,
  paginate,
  unique,
  items,
  label,
  disablePreviousDate,
  disableFutureDate,
  initials,
  fields,
  onField,
  schema,
  isIgnore,
  position,
  maxWidth,
  titleModal,
  checkbox,
  radio,
  create,
  remove,
  icons,
  variant,
  showPagination,
  onCallback,
  noCreate,
  noRemove,
  onRow,
  loading,
  onOpenForm,
}) {
  const [data, setData] = useState(initials);
  const [head, setHead] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState([]);

  const tableRef = useRef(null);

  const handleOpenModal = useCallback(
    (params) => {
      if (isEmpty(fields)) {
        if (date) setIsOpen(true);
        else tableRef.current.handleOpenModal();
      } else {
        tableRef.current.handleOpenModal({ initials: params });
        onOpenForm(params)
      }
    },
    [date, fields]
  );

  const handleAddDateTime = useCallback((isDate) => {
    setData((state) => [...state, { date: isDate }]);
  }, []);

  const handleAddItem = useCallback((value) => {
    setData((state) => [...state, value]);
    tableRef.current.handleCloseModal();
  }, []);

  const handleModifyItem = useCallback(
    (value) => {
      const key = findIndex(data, (d) => d[unique] === value[unique]);

      setData((state) =>
        state.map((rest, index) => {
          if (index === key) return value;
          return rest;
        })
      );

      tableRef.current.handleCloseModal();
      setSelected([]);
    },
    [data, unique]
  );

  const handleSelectItems = useCallback((values) => {
    setSelected(values);
  }, []);

  const handleRemoveItems = useCallback(() => {
    onCallback({ type: 'remove', data: selected });
    setData((state) => differenceBy(state, selected, unique));
    setSelected([]);
  }, [selected, unique]);

  useEffect(() => {
    if (!isEmpty(cells) && !date) setHead(cells);
    if (date) setHead([{ id: 'date', label: 'Data' }]);
  }, [cells, date]);

  useEffect(() => {
    onChanged(data);
  }, [data]);

  useEffect(() => {
    setData(initials);
  }, [initials]);

  return (
    <>
      <S.Wrapper position={position}>
        <S.GroupButtons position={position}>
          {create && !noCreate && (
            <Button
              color="primary"
              variant={variant}
              size="small"
              startIcon={icons ? <Add /> : undefined}
              onClick={() => handleOpenModal({})}
              disabled={loading}
            >
              {t('s.geral.botaoincluir')}
            </Button>
          )}

          {!isEmpty(fields) && (
            <Button
              color="primary"
              variant={variant}
              size="small"
              startIcon={icons ? <Refresh /> : undefined}
              disabled={selected.length !== 1 || loading}
              onClick={() => handleOpenModal(selected[0])}
            >
              {t('s.geral.botaoatualizar')}
            </Button>
          )}

          {remove && !noRemove && (
            <Button
              color="secondary"
              variant={variant}
              size="small"
              startIcon={icons ? <Remove /> : undefined}
              disabled={isEmpty(selected) || loading}
              onClick={handleRemoveItems}
            >
              {t('s.geral.botaoexcluir')}
            </Button>
          )}
        </S.GroupButtons>

        <Table
          showPagination={showPagination}
          checkbox={checkbox && !radio}
          radio={radio}
          noButton
          cells={head}
          data={data}
          clearCheckeds={isEmpty(selected)}
          height={`${height}rem`}
          size={size}
          title={title}
          showCount={showCount}
          paginate={paginate}
          unique={unique}
          onCallback={handleSelectItems}
          onRow={onRow}
        />
      </S.Wrapper>

      <MDate
        time={time}
        open={isOpen}
        onClose={setIsOpen}
        onSubmit={handleAddDateTime}
        items={data}
        disablePreviousDate={disablePreviousDate}
        disableFutureDate={disableFutureDate}
      />

      <Modal
        ref={tableRef}
        form
        disableBackdropClick
        disableEscapeKeyDown
        maxWidth={maxWidth}
        title={titleModal}
        component={(props) => (
          <MTable
            {...props}
            items={items}
            ignore={data}
            onChangeItem={handleAddItem}
            onModify={handleModifyItem}
            label={label}
            fields={fields}
            onField={onField}
            isSchema={schema}
            isIgnore={isIgnore}
            onCallback={onCallback}
          />
        )}
      />
    </>
  );
}

SelectDataInTable.propTypes = {
  date: PropTypes.bool,
  time: PropTypes.bool,
  height: PropTypes.number,
  cells: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      format: PropTypes.func,
    })
  ).isRequired,
  size: PropTypes.oneOf(['small', 'medium']),
  onChanged: PropTypes.func.isRequired,
  title: PropTypes.string,
  showCount: PropTypes.bool,
  paginate: PropTypes.number,
  unique: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType(PropTypes.string, PropTypes.number).isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType(PropTypes.string, PropTypes.number).isRequired,
    })
  ),
  label: PropTypes.string,
  disablePreviousDate: PropTypes.bool,
  disableFutureDate: PropTypes.bool,
  initials: PropTypes.arrayOf(PropTypes.any),
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      options: PropTypes.arrayOf(),
      disabled: PropTypes.bool,
    })
  ),
  onField: PropTypes.func,
  schema: PropTypes.objectOf(PropTypes.any),
  isIgnore: PropTypes.string,
  position: PropTypes.oneOf(['row', 'column']),
  maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
  titleModal: PropTypes.string,
  checkbox: PropTypes.bool,
  radio: PropTypes.bool,
  create: PropTypes.bool,
  remove: PropTypes.bool,
  icons: PropTypes.bool,
  variant: PropTypes.oneOf(['contained', 'outlined', 'text']),
  showPagination: PropTypes.bool,
  onCallback: PropTypes.func,
  noCreate: PropTypes.bool,
  noRemove: PropTypes.bool,
  onRow: PropTypes.func,
  onOpenForm: PropTypes.func,
  loading: PropTypes.bool,
};

SelectDataInTable.defaultProps = {
  date: false,
  time: false,
  height: 20,
  size: 'medium',
  title: '',
  showCount: false,
  paginate: 5,
  unique: 'date',
  items: [],
  label: '',
  disablePreviousDate: false,
  disableFutureDate: false,
  initials: [],
  fields: {},
  onField: (value) => value,
  schema: {},
  isIgnore: '',
  position: 'column',
  maxWidth: 'md',
  titleModal: '',
  checkbox: true,
  radio: false,
  create: true,
  remove: true,
  icons: true,
  variant: 'outlined',
  showPagination: true,
  onCallback: (value) => value,
  noCreate: false,
  noRemove: false,
  onRow: () => undefined,
  onOpenForm: () => undefined,
  loading: false,
};
